import { useState, useEffect } from 'react';
import { Search, Clock, AlertCircle, CheckCircle2, Users, Briefcase, UserCheck, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const KioskView = () => {
  const [mode, setMode] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationData, setConfirmationData] = useState(null);
  const [note, setNote] = useState('');
  const [showNoteDialog, setShowNoteDialog] = useState(false);
  const [clockAction, setClockAction] = useState(null);
  const [showIssueReport, setShowIssueReport] = useState(false);
  const [issueMessage, setIssueMessage] = useState('');
  const [visitorForm, setVisitorForm] = useState({ name: '', company: '', registration: '', visiting: '' });
  const [contractorForm, setContractorForm] = useState({ name: '', company: '', registration: '', visiting: '', validFrom: '', validTo: '', willReturn: false });
  const [employees, setEmployees] = useState([]);
  const [returningContractors, setReturningContractors] = useState([]);
  const [activeVisitors, setActiveVisitors] = useState([]);
  const [activeContractors, setActiveContractors] = useState([]);
  const [showReturningContractors, setShowReturningContractors] = useState(false);
  const [signOutMode, setSignOutMode] = useState(false);
  const [employeeSearchQuery, setEmployeeSearchQuery] = useState('');
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [showEmergencyPinDialog, setShowEmergencyPinDialog] = useState(false);
  const [emergencyPin, setEmergencyPin] = useState('');
  const [showEmergencyRegister, setShowEmergencyRegister] = useState(false);
  const [hqEmployees, setHqEmployees] = useState([]);

  // Ensure proper PWA standalone mode setup
  useEffect(() => {
    // Set page title for kiosk mode
    document.title = 'Reuseabox Kiosk';
    
    // Update meta tags for kiosk mode
    const themeColor = document.querySelector('meta[name="theme-color"]');
    if (themeColor) {
      themeColor.content = '#007B3A';
    }
  }, []);

  useEffect(() => {
    if (mode === 'visitor' || mode === 'contractor') {
      loadEmployees();
    }
    if (mode === 'visitor') {
      loadActiveVisitors();
    }
    if (mode === 'contractor') {
      loadReturningContractors();
      loadActiveContractors();
    }
  }, [mode]);

  const loadEmployees = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/kiosk/employees-list`);
      const data = await res.json();
      setEmployees(data);
    } catch (error) {
      console.error('Failed to load employees:', error);
    }
  };

  const loadReturningContractors = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/kiosk/returning-contractors`);
      const data = await res.json();
      setReturningContractors(data);
    } catch (error) {
      console.error('Failed to load contractors:', error);
    }
  };

  const loadActiveVisitors = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/kiosk/active-visitors`);
      const data = await res.json();
      setActiveVisitors(data);
    } catch (error) {
      console.error('Failed to load active visitors:', error);
    }
  };

  const loadActiveContractors = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/kiosk/active-contractors`);
      const data = await res.json();
      setActiveContractors(data);
    } catch (error) {
      console.error('Failed to load active contractors:', error);
    }
  };

  const resetToHome = () => {
    setMode(null);
    setSearchQuery('');
    setSearchResults([]);
    setSelectedPerson(null);
    setVisitorForm({ name: '', company: '', registration: '', visiting: '' });
    setContractorForm({ name: '', company: '', registration: '', visiting: '', validFrom: '', validTo: '', willReturn: false });
    setShowReturningContractors(false);
    setSignOutMode(false);
    setEmployeeSearchQuery('');
    setFilteredEmployees([]);
  };

  const handleEmployeeSearch = (query) => {
    setEmployeeSearchQuery(query);
    if (query.length >= 3) {
      const filtered = employees.filter(emp => {
        const fullName = `${emp.first_name} ${emp.last_name}`.toLowerCase();
        return fullName.includes(query.toLowerCase());
      });
      setFilteredEmployees(filtered);
    } else {
      setFilteredEmployees([]);
    }
  };

  const selectEmployeeForVisiting = (emp) => {
    if (mode === 'visitor') {
      setVisitorForm({...visitorForm, visiting: emp.id});
    } else if (mode === 'contractor') {
      setContractorForm({...contractorForm, visiting: emp.id});
    }
    setEmployeeSearchQuery(`${emp.first_name} ${emp.last_name}`);
    setFilteredEmployees([]);
  };

  const handleSearch = async (query) => {
    const searchTerm = query || searchQuery;
    if (!searchTerm.trim() || searchTerm.length < 3) {
      setSearchResults([]);
      return;
    }
    setIsSearching(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/kiosk/search?query=${encodeURIComponent(searchTerm)}`);
      const data = await res.json();
      setSearchResults(data);
    } catch (error) {
      console.error('Search failed');
    } finally {
      setIsSearching(false);
    }
  };

  const selectEmployee = (employee) => {
    setSelectedPerson(employee);
    setSearchResults([]);
    setSearchQuery(`${employee.first_name} ${employee.last_name}`);
  };

  const handleEmployeeClockAction = async (action) => {
    if (!selectedPerson) return;
    if (action === 'out') {
      setClockAction('out');
      setShowNoteDialog(true);
      return;
    }
    await performEmployeeClockAction(action, '');
  };

  const performEmployeeClockAction = async (action, noteText = '') => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/kiosk/clock`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ employee_id: selectedPerson.id, action: action, note: noteText || undefined })
      });
      const data = await res.json();
      if (data.error) {
        alert(data.message);
        return;
      }
      if (data.success) {
        setConfirmationData({ 
          type: 'employee', 
          action: data.action, 
          timestamp: data.timestamp,
          location: data.location,
          hours_worked_today: data.hours_worked_today,
          scheduled_shift: data.scheduled_shift,
          person: data.employee 
        });
        setShowConfirmation(true);
        setTimeout(() => {
          setShowConfirmation(false);
          resetToHome();
        }, 3000);
      }
    } catch (error) {
      console.error('Clock action failed');
    }
  };

  const handleNoteSubmit = async () => {
    setShowNoteDialog(false);
    await performEmployeeClockAction(clockAction, note);
    setNote('');
  };

  const handleVisitorSignIn = async () => {
    if (!visitorForm.name || !visitorForm.company || !visitorForm.visiting) {
      alert('Please fill in all required fields');
      return;
    }
    try {
      const res = await fetch(`${BACKEND_URL}/api/kiosk/visitor-clock`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'in', ...visitorForm })
      });
      const data = await res.json();
      if (data.success) {
        setConfirmationData({ type: 'visitor', action: 'sign_in', timestamp: new Date(data.timestamp).toLocaleString(), person: { name: visitorForm.name, company: visitorForm.company } });
        setShowConfirmation(true);
        setTimeout(() => {
          setShowConfirmation(false);
          resetToHome();
        }, 5000);
      }
    } catch (error) {
      console.error('Sign in failed');
    }
  };

  const handleVisitorSignOut = async (visitor) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/kiosk/visitor-clock`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'out', visitor_id: visitor.id })
      });
      const data = await res.json();
      if (data.success) {
        setConfirmationData({ type: 'visitor', action: 'sign_out', timestamp: new Date(data.timestamp).toLocaleString(), person: { name: visitor.name, company: visitor.company } });
        setShowConfirmation(true);
        setTimeout(() => {
          setShowConfirmation(false);
          resetToHome();
        }, 5000);
      }
    } catch (error) {
      console.error('Sign out failed');
    }
  };

  const handleContractorSignIn = async (isReturning = false, contractorId = null) => {
    const formData = isReturning ? {} : contractorForm;
    if (!isReturning && (!formData.name || !formData.company || !formData.visiting)) {
      alert('Please fill in all required fields');
      return;
    }
    try {
      const res = await fetch(`${BACKEND_URL}/api/kiosk/contractor-clock`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'in',
          contractor_id: contractorId,
          is_returning: isReturning,
          ...(!isReturning && {
            name: formData.name,
            company: formData.company,
            registration: formData.registration,
            visiting: formData.visiting,
            ...(formData.willReturn && { valid_from: formData.validFrom, valid_to: formData.validTo })
          })
        })
      });
      const data = await res.json();
      if (data.success) {
        setConfirmationData({ type: 'contractor', action: 'sign_in', timestamp: new Date(data.timestamp).toLocaleString(), person: data.contractor });
        setShowConfirmation(true);
        setTimeout(() => {
          setShowConfirmation(false);
          resetToHome();
        }, 5000);
      }
    } catch (error) {
      console.error('Sign in failed');
    }
  };

  const handleContractorSignOut = async (contractor) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/kiosk/contractor-clock`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'out', contractor_id: contractor.id })
      });
      const data = await res.json();
      if (data.success) {
        setConfirmationData({ type: 'contractor', action: 'sign_out', timestamp: new Date(data.timestamp).toLocaleString(), person: contractor });
        setShowConfirmation(true);
        setTimeout(() => {
          setShowConfirmation(false);
          resetToHome();
        }, 5000);
      }
    } catch (error) {
      console.error('Sign out failed');
    }
  };

  const handleReportIssue = async () => {
    if (!issueMessage.trim()) return;
    try {
      await fetch(`${BACKEND_URL}/api/kiosk/report-issue`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ employee_name: selectedPerson ? `${selectedPerson.first_name} ${selectedPerson.last_name}` : null, message: issueMessage })
      });
      alert('Issue reported successfully');
      setShowIssueReport(false);
      setIssueMessage('');
    } catch (error) {
      alert('Failed to report issue');
    }
  };

  const handleEmergencyPinSubmit = async () => {
    const CORRECT_PIN = '626476';
    
    if (emergencyPin === CORRECT_PIN) {
      // Fetch employees currently clocked in at HQ
      try {
        const response = await fetch(`${BACKEND_URL}/api/kiosk/hq-employees`);
        if (response.ok) {
          const data = await response.json();
          setHqEmployees(data.employees || []);
          setShowEmergencyPinDialog(false);
          setShowEmergencyRegister(true);
          setEmergencyPin('');
        }
      } catch (error) {
        console.error('Failed to fetch HQ employees:', error);
        alert('Failed to load employee list');
      }
    } else {
      alert('Incorrect PIN. Please try again.');
      setEmergencyPin('');
    }
  };

  const handleEmergencyRegisterClose = () => {
    setShowEmergencyRegister(false);
    setHqEmployees([]);
  };

  // Home Screen
  if (!mode && !showConfirmation) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 overflow-hidden" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Inter", "Helvetica Neue", Arial, sans-serif', touchAction: 'manipulation' }}>
        {/* Top Bar with Logo - iPad optimized */}
        <div className="w-full bg-[#00461A] py-4 md:py-6 shadow-lg">
          <div className="flex justify-center items-center">
            <img 
              src="/reuseabox-gradient-logo.svg" 
              alt="Reuseabox" 
              className="h-12 md:h-16 w-auto"
            />
          </div>
        </div>

        {/* Main Content - iPad optimized */}
        <div className="flex items-center justify-center" style={{ height: 'calc(100vh - 180px)' }}>
          <div className="w-full max-w-4xl px-4 md:px-6">
            <div className="text-center mb-8 md:mb-10">
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-2 md:mb-3" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Inter", "Helvetica Neue", Arial, sans-serif', color: '#00461A' }}>
                WE'RE SO GLAD
              </h2>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4 md:mb-6" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Inter", "Helvetica Neue", Arial, sans-serif', color: '#00461A' }}>
                YOU'RE HERE
              </h2>
              <p className="text-xl md:text-2xl text-gray-700 mt-4 md:mt-6" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Inter", "Helvetica Neue", Arial, sans-serif' }}>
                Please sign in before you enter:
              </p>
            </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6 mb-6 md:mb-8">
            <button onClick={() => setMode('employee')} className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl p-6 md:p-8 active:scale-95 transition-all touch-manipulation min-h-[140px]">
              <div className="w-14 h-14 md:w-16 md:h-16 mx-auto mb-3 md:mb-4 bg-gradient-to-br from-teal-400 to-emerald-500 rounded-2xl flex items-center justify-center">
                <UserCheck className="w-7 h-7 md:w-8 md:h-8 text-white" />
              </div>
              <h3 className="text-xl md:text-2xl font-bold text-emerald-900 mb-1 md:mb-2">Employee</h3>
              <p className="text-base md:text-lg text-emerald-700">Clock In / Clock Out</p>
            </button>
            <button onClick={() => setMode('visitor')} className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl p-6 md:p-8 active:scale-95 transition-all touch-manipulation min-h-[140px]">
              <div className="w-14 h-14 md:w-16 md:h-16 mx-auto mb-3 md:mb-4 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-2xl flex items-center justify-center">
                <Users className="w-7 h-7 md:w-8 md:h-8 text-white" />
              </div>
              <h3 className="text-xl md:text-2xl font-bold text-emerald-900 mb-1 md:mb-2">Visitor</h3>
              <p className="text-base md:text-lg text-emerald-700">Sign In / Sign Out</p>
            </button>
            <button onClick={() => setMode('contractor')} className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl p-6 md:p-8 active:scale-95 transition-all touch-manipulation min-h-[140px]">
              <div className="w-14 h-14 md:w-16 md:h-16 mx-auto mb-3 md:mb-4 bg-gradient-to-br from-amber-400 to-orange-500 rounded-2xl flex items-center justify-center">
                <Briefcase className="w-7 h-7 md:w-8 md:h-8 text-white" />
              </div>
              <h3 className="text-xl md:text-2xl font-bold text-emerald-900 mb-1 md:mb-2">Contractor</h3>
              <p className="text-base md:text-lg text-emerald-700">Sign In / Sign Out</p>
            </button>
          </div>
          </div>
        </div>

        {/* Footer with buttons - iPad optimized */}
        <div className="fixed bottom-0 left-0 right-0 bg-[#28CFB3] py-4 md:py-5 shadow-lg">
          <div className="max-w-4xl mx-auto px-4 md:px-6 flex flex-col sm:flex-row justify-center gap-3 md:gap-6">
            <Button 
              onClick={() => setShowIssueReport(true)} 
              variant="outline" 
              className="h-12 md:h-14 px-6 md:px-8 bg-white/90 backdrop-blur-sm border-2 border-white text-gray-800 active:bg-white text-base md:text-lg font-semibold shadow-lg touch-manipulation"
              style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Inter", "Helvetica Neue", Arial, sans-serif' }}
            >
              <AlertCircle className="w-4 h-4 md:w-5 md:h-5 mr-2" />
              Report an Issue
            </Button>
            <Button 
              onClick={() => setShowEmergencyPinDialog(true)} 
              variant="outline" 
              className="h-12 md:h-14 px-6 md:px-8 bg-white/90 backdrop-blur-sm border-2 border-white text-gray-800 active:bg-white text-base md:text-lg font-semibold shadow-lg touch-manipulation"
              style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Inter", "Helvetica Neue", Arial, sans-serif' }}
            >
              <AlertCircle className="w-4 h-4 md:w-5 md:h-5 mr-2" />
              Emergency Register
            </Button>
          </div>
        </div>

        <Dialog open={showIssueReport} onOpenChange={setShowIssueReport}>
          <DialogContent style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Inter", "Helvetica Neue", Arial, sans-serif' }}>
            <DialogHeader>
              <DialogTitle style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Inter", "Helvetica Neue", Arial, sans-serif' }}>Report an Issue</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <Textarea placeholder="Describe the issue..." value={issueMessage} onChange={(e) => setIssueMessage(e.target.value)} rows={5} style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Inter", "Helvetica Neue", Arial, sans-serif' }} />
              <Button onClick={handleReportIssue} className="w-full bg-teal-500 hover:bg-teal-600" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Inter", "Helvetica Neue", Arial, sans-serif' }}>Submit Issue</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Emergency Register PIN Dialog */}
        <Dialog open={showEmergencyPinDialog} onOpenChange={setShowEmergencyPinDialog}>
          <DialogContent style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Inter", "Helvetica Neue", Arial, sans-serif' }}>
            <DialogHeader>
              <DialogTitle style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Inter", "Helvetica Neue", Arial, sans-serif' }}>Emergency Register</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-gray-700" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Inter", "Helvetica Neue", Arial, sans-serif' }}>Please enter the PIN to access the emergency register:</p>
              <Input 
                type="password" 
                placeholder="Enter PIN" 
                value={emergencyPin} 
                onChange={(e) => setEmergencyPin(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleEmergencyPinSubmit()}
                className="text-center text-2xl tracking-widest"
                maxLength={6}
                style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Inter", "Helvetica Neue", Arial, sans-serif' }}
              />
              <div className="flex gap-3">
                <Button onClick={() => { setShowEmergencyPinDialog(false); setEmergencyPin(''); }} variant="outline" className="flex-1" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Inter", "Helvetica Neue", Arial, sans-serif' }}>Cancel</Button>
                <Button onClick={handleEmergencyPinSubmit} className="flex-1 bg-[#00461A] hover:bg-[#003515] text-white" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Inter", "Helvetica Neue", Arial, sans-serif' }}>Submit</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Emergency Register List */}
        <Dialog open={showEmergencyRegister} onOpenChange={handleEmergencyRegisterClose}>
          <DialogContent className="max-w-3xl max-h-[80vh]" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Inter", "Helvetica Neue", Arial, sans-serif' }}>
            <DialogHeader>
              <DialogTitle style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Inter", "Helvetica Neue", Arial, sans-serif' }}>Emergency Register - Staff at HQ</DialogTitle>
            </DialogHeader>
            <div className="space-y-2 overflow-y-auto max-h-[60vh]">
              {hqEmployees.length === 0 ? (
                <p className="text-center text-gray-500 py-8" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Inter", "Helvetica Neue", Arial, sans-serif' }}>No employees currently clocked in at HQ</p>
              ) : (
                hqEmployees.map((employee, index) => (
                  <div key={index} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-semibold text-lg" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Inter", "Helvetica Neue", Arial, sans-serif' }}>
                          {employee.first_name} {employee.last_name}
                        </h3>
                        <p className="text-sm text-gray-600" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Inter", "Helvetica Neue", Arial, sans-serif' }}>
                          {employee.department || 'No department'}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-500" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Inter", "Helvetica Neue", Arial, sans-serif' }}>
                          Clocked in: {employee.clock_in_time}
                        </p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
            <DialogFooter>
              <Button onClick={handleEmergencyRegisterClose} className="w-full bg-[#00461A] hover:bg-[#003515] text-white" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Inter", "Helvetica Neue", Arial, sans-serif' }}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Confirmation Screen
  if (showConfirmation) {
    const formatTime = (timestamp) => {
      const date = new Date(timestamp);
      return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
    };

    const formatHoursWorked = (hours) => {
      const h = Math.floor(hours);
      const m = Math.round((hours - h) * 60);
      return `${h}h ${m.toString().padStart(2, '0')}m`;
    };

    return (
      <div 
        className="fixed inset-0 flex items-center justify-center z-50"
        style={{ 
          backgroundColor: 'rgba(230, 243, 238, 0.85)',
          backdropFilter: 'blur(2px)'
        }}
      >
        <div 
          className="bg-white overflow-hidden text-center"
          style={{
            width: '440px',
            maxWidth: '440px',
            minWidth: '440px',
            borderRadius: '28px',
            boxShadow: '0 16px 40px rgba(0, 0, 0, 0.12)',
            padding: '0 32px 40px'
          }}
        >
          {/* Green top bar - full width using negative margin */}
          <div 
            style={{
              height: '52px',
              backgroundColor: '#007B3A',
              margin: '0 -32px 24px'
            }}
          />
          
          {/* Checkmark icon */}
          <div 
            className="mx-auto flex items-center justify-center"
            style={{
              width: '72px',
              height: '72px',
              backgroundColor: '#00A651',
              borderRadius: '50%',
              marginBottom: '20px'
            }}
          >
            <CheckCircle2 style={{ width: '40px', height: '40px', color: 'white' }} />
          </div>
          
          {/* Success message */}
          <h2 
            style={{
              fontSize: '24px',
              fontWeight: 600,
              color: '#0F1A24',
              margin: '0 0 6px',
              lineHeight: '1.3'
            }}
          >
            {confirmationData?.action === 'clock_in' || confirmationData?.action === 'sign_in' 
              ? `${confirmationData.type === 'employee' ? 'Clock-in' : 'Sign-in'} successful` 
              : `${confirmationData.type === 'employee' ? 'Clock-out' : 'Sign-out'} successful`}
          </h2>
          
          {/* User name */}
          <p 
            style={{
              fontSize: '18px',
              color: '#1A1A1A',
              margin: '0 0 20px'
            }}
          >
            {confirmationData?.person.first_name ? `${confirmationData.person.first_name} ${confirmationData.person.last_name}` : confirmationData?.person.name}
          </p>
          
          {/* Profile image */}
          <div 
            className="mx-auto"
            style={{
              width: '80px',
              height: '80px',
              marginBottom: '24px',
              display: 'flex',
              justifyContent: 'center'
            }}
          >
            {(confirmationData?.person.profile_image || confirmationData?.person.profile_image_path) ? (
              <img 
                src={confirmationData.person.profile_image || confirmationData.person.profile_image_path} 
                alt={`${confirmationData.person.first_name} ${confirmationData.person.last_name}`}
                style={{
                  width: '100%',
                  height: '100%',
                  borderRadius: '50%',
                  objectFit: 'cover',
                  border: '3px solid #F5F9F8',
                  boxShadow: '0 6px 14px rgba(0, 0, 0, 0.18)'
                }}
              />
            ) : (
              <div 
                style={{
                  width: '100%',
                  height: '100%',
                  backgroundColor: '#00A8A0',
                  borderRadius: '50%',
                  color: 'white',
                  fontSize: '28px',
                  fontWeight: 700,
                  border: '3px solid #F5F9F8',
                  boxShadow: '0 6px 14px rgba(0, 0, 0, 0.18)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                {confirmationData?.person.first_name?.[0] || confirmationData?.person.name?.[0]}{confirmationData?.person.last_name?.[0] || confirmationData?.person.name?.[1]}
              </div>
            )}
          </div>
          
          {/* Clock-in Time */}
          <p 
            style={{
              fontSize: '32px',
              fontWeight: 700,
              color: '#0F1A24',
              margin: '0 0 2px'
            }}
          >
            {formatTime(confirmationData?.timestamp)}
          </p>
          
          {/* Kiosk Name */}
          <p 
            style={{
              fontSize: '14px',
              color: '#777777',
              margin: '0 0 24px'
            }}
          >
            {confirmationData?.location ? `${confirmationData.location} Kiosk` : 'HQ Kiosk'}
          </p>
          
          {/* Divider */}
          <div style={{ height: '1px', background: '#E3E8EC', margin: '20px 0' }} />
          
          {/* Shift and hours info - only for clock-in */}
          {confirmationData?.action === 'clock_in' && confirmationData?.type === 'employee' && (
            <div>
              {confirmationData?.scheduled_shift && (
                <p style={{ fontSize: '15px', fontWeight: 500, color: '#1B2733', margin: '3px 0' }}>
                  Scheduled shift: {confirmationData.scheduled_shift.start}–{confirmationData.scheduled_shift.end}
                </p>
              )}
              <p style={{ fontSize: '15px', fontWeight: 500, color: '#1B2733', margin: '3px 0' }}>
                Hours worked so far today: {formatHoursWorked(confirmationData?.hours_worked_today || 0)}
              </p>
            </div>
          )}
          
          {/* Visitor/Contractor company */}
          {confirmationData?.person.company && (
            <p style={{ fontSize: '15px', fontWeight: 500, color: '#1B2733', margin: '3px 0' }}>
              {confirmationData.person.company}
            </p>
          )}
          
          {/* Bottom Divider */}
          <div style={{ height: '1px', background: '#E3E8EC', margin: '20px 0' }} />
          
          {/* Auto-close message */}
          <p 
            style={{
              fontSize: '14px',
              color: '#666666',
              marginTop: '12px'
            }}
          >
            This screen will close in 3 seconds...
          </p>
        </div>
      </div>
    );
  }

  // Employee Mode
  if (mode === 'employee') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 flex items-center justify-center p-6">
        <div className="w-full max-w-4xl">
          <div className="text-center mb-8">
            <Button onClick={resetToHome} variant="ghost" className="mb-4 text-emerald-700 hover:text-emerald-900">
              <ArrowLeft className="w-5 h-5 mr-2" />Back to Home
            </Button>
            <h1 className="text-5xl font-bold text-emerald-900 mb-2">Employee Clock In/Out</h1>
          </div>
          <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-2xl p-8">
            <div className="mb-8">
              <label className="block text-lg font-semibold text-emerald-900 mb-3">Search by Name</label>
              <div className="relative">
                <Input 
                  type="text" 
                  placeholder="Start typing name (min 3 letters)..." 
                  value={searchQuery} 
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    handleSearch(e.target.value);
                  }} 
                  className="text-lg h-14 border-2 border-emerald-200" 
                />
                {searchResults.length > 0 && (
                  <div className="absolute z-10 w-full mt-2 bg-white border-2 border-emerald-200 rounded-xl shadow-2xl max-h-80 overflow-y-auto">
                    {searchResults.map((emp) => (
                      <button 
                        key={emp.id} 
                        onClick={() => selectEmployee(emp)} 
                        className="w-full flex items-center gap-4 p-4 hover:bg-emerald-50 border-b last:border-b-0 transition-colors"
                      >
                        <div className="w-12 h-12 bg-gradient-to-br from-teal-400 to-emerald-500 rounded-full flex items-center justify-center text-white font-bold overflow-hidden">
                          {emp.profile_image ? (
                            <img 
                              src={emp.profile_image} 
                              alt={`${emp.first_name} ${emp.last_name}`}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <span>{emp.first_name[0]}{emp.last_name[0]}</span>
                          )}
                        </div>
                        <div className="text-left">
                          <div className="font-semibold text-emerald-900">{emp.first_name} {emp.last_name}</div>
                          <div className="text-sm text-emerald-600">{emp.department}</div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            {selectedPerson && (
              <div className="p-6 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-2xl border-2 border-teal-200">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-20 h-20 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-2xl flex items-center justify-center text-white font-bold text-2xl overflow-hidden">
                    {selectedPerson.profile_image ? (
                      <img 
                        src={selectedPerson.profile_image} 
                        alt={`${selectedPerson.first_name} ${selectedPerson.last_name}`}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span>{selectedPerson.first_name[0]}{selectedPerson.last_name[0]}</span>
                    )}
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-emerald-900">{selectedPerson.first_name} {selectedPerson.last_name}</h3>
                    <p className="text-emerald-600">Ready to clock in/out</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <Button onClick={() => handleEmployeeClockAction('in')} className="h-16 text-xl font-bold bg-gradient-to-r from-teal-500 to-emerald-600">
                    <Clock className="w-6 h-6 mr-2" />Clock In
                  </Button>
                  <Button onClick={() => handleEmployeeClockAction('out')} className="h-16 text-xl font-bold bg-gradient-to-r from-orange-500 to-amber-600">
                    <Clock className="w-6 h-6 mr-2" />Clock Out
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
        <Dialog open={showNoteDialog} onOpenChange={setShowNoteDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Note (Optional)</DialogTitle>
            </DialogHeader>
            <Textarea placeholder="Add a note..." value={note} onChange={(e) => setNote(e.target.value)} rows={4} />
            <DialogFooter>
              <Button onClick={() => { setShowNoteDialog(false); setNote(''); }} variant="outline">Skip</Button>
              <Button onClick={handleNoteSubmit} className="bg-teal-500">Confirm</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Visitor Mode
  if (mode === 'visitor') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 flex items-center justify-center p-6">
        <div className="w-full max-w-4xl">
          <div className="text-center mb-8">
            <Button onClick={resetToHome} variant="ghost" className="mb-4 text-emerald-700 hover:text-emerald-900">
              <ArrowLeft className="w-5 h-5 mr-2" />Back to Home
            </Button>
            <h1 className="text-5xl font-bold text-emerald-900 mb-2">Visitor Sign In/Out</h1>
          </div>
          <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-2xl p-8">
            <div className="grid grid-cols-2 gap-4 mb-6">
              <Button onClick={() => setSignOutMode(false)} variant={!signOutMode ? 'default' : 'outline'} className={!signOutMode ? 'bg-teal-500' : ''}>
                Sign In
              </Button>
              <Button onClick={() => setSignOutMode(true)} variant={signOutMode ? 'default' : 'outline'} className={signOutMode ? 'bg-orange-500' : ''}>
                Sign Out
              </Button>
            </div>
            {!signOutMode ? (
              <div className="space-y-4">
                <div>
                  <Label>Name *</Label>
                  <Input placeholder="Full name" value={visitorForm.name} onChange={(e) => setVisitorForm({...visitorForm, name: e.target.value})} className="h-12" />
                </div>
                <div>
                  <Label>Company *</Label>
                  <Input placeholder="Company name" value={visitorForm.company} onChange={(e) => setVisitorForm({...visitorForm, company: e.target.value})} className="h-12" />
                </div>
                <div>
                  <Label>Vehicle Registration</Label>
                  <Input placeholder="e.g. AB12 CDE" value={visitorForm.registration} onChange={(e) => setVisitorForm({...visitorForm, registration: e.target.value})} className="h-12" />
                </div>
                <div>
                  <Label>Who are you visiting? *</Label>
                  <div className="relative">
                    <Input 
                      placeholder="Start typing name (min 3 letters)..." 
                      value={employeeSearchQuery} 
                      onChange={(e) => handleEmployeeSearch(e.target.value)} 
                      className="h-12" 
                    />
                    {filteredEmployees.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                        {filteredEmployees.map((emp) => (
                          <button
                            key={emp.id}
                            type="button"
                            onClick={() => selectEmployeeForVisiting(emp)}
                            className="w-full px-4 py-3 text-left hover:bg-emerald-50 border-b last:border-b-0"
                          >
                            <div className="font-semibold text-emerald-900">{emp.first_name} {emp.last_name}</div>
                            <div className="text-sm text-emerald-600">{emp.department}</div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <Button onClick={handleVisitorSignIn} className="w-full h-14 text-xl bg-teal-500 hover:bg-teal-600">Sign In</Button>
              </div>
            ) : (
              <div>
                <p className="text-emerald-700 mb-4">Select a visitor to sign out:</p>
                {activeVisitors.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">No active visitors</p>
                ) : (
                  <div className="space-y-2">
                    {activeVisitors.map((visitor) => (
                      <button key={visitor.id} onClick={() => handleVisitorSignOut(visitor)} className="w-full p-4 bg-emerald-50 hover:bg-emerald-100 rounded-lg text-left">
                        <div className="font-bold text-emerald-900">{visitor.name}</div>
                        <div className="text-sm text-emerald-600">{visitor.company}</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Contractor Mode
  if (mode === 'contractor') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 flex items-center justify-center p-6">
        <div className="w-full max-w-4xl">
          <div className="text-center mb-8">
            <Button onClick={resetToHome} variant="ghost" className="mb-4 text-emerald-700 hover:text-emerald-900">
              <ArrowLeft className="w-5 h-5 mr-2" />Back to Home
            </Button>
            <h1 className="text-5xl font-bold text-emerald-900 mb-2">Contractor Sign In/Out</h1>
          </div>
          <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-2xl p-8">
            <div className="grid grid-cols-2 gap-4 mb-6">
              <Button onClick={() => { setSignOutMode(false); setShowReturningContractors(false); }} variant={!signOutMode && !showReturningContractors ? 'default' : 'outline'} className={!signOutMode && !showReturningContractors ? 'bg-teal-500' : ''}>
                New Contractor
              </Button>
              <Button onClick={() => { setSignOutMode(false); setShowReturningContractors(true); }} variant={showReturningContractors ? 'default' : 'outline'} className={showReturningContractors ? 'bg-teal-500' : ''}>
                Returning
              </Button>
            </div>
            <div className="grid grid-cols-1 gap-4 mb-6">
              <Button onClick={() => { setSignOutMode(true); setShowReturningContractors(false); }} variant={signOutMode ? 'default' : 'outline'} className={signOutMode ? 'bg-orange-500' : ''}>
                Sign Out
              </Button>
            </div>
            {!signOutMode && !showReturningContractors && (
              <div className="space-y-4">
                <div>
                  <Label>Name *</Label>
                  <Input placeholder="Full name" value={contractorForm.name} onChange={(e) => setContractorForm({...contractorForm, name: e.target.value})} className="h-12" />
                </div>
                <div>
                  <Label>Company *</Label>
                  <Input placeholder="Company name" value={contractorForm.company} onChange={(e) => setContractorForm({...contractorForm, company: e.target.value})} className="h-12" />
                </div>
                <div>
                  <Label>Vehicle Registration</Label>
                  <Input placeholder="e.g. AB12 CDE" value={contractorForm.registration} onChange={(e) => setContractorForm({...contractorForm, registration: e.target.value})} className="h-12" />
                </div>
                <div>
                  <Label>Point of Contact *</Label>
                  <div className="relative">
                    <Input 
                      placeholder="Start typing name (min 3 letters)..." 
                      value={employeeSearchQuery} 
                      onChange={(e) => handleEmployeeSearch(e.target.value)} 
                      className="h-12" 
                    />
                    {filteredEmployees.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                        {filteredEmployees.map((emp) => (
                          <button
                            key={emp.id}
                            type="button"
                            onClick={() => selectEmployeeForVisiting(emp)}
                            className="w-full px-4 py-3 text-left hover:bg-emerald-50 border-b last:border-b-0"
                          >
                            <div className="font-semibold text-emerald-900">{emp.first_name} {emp.last_name}</div>
                            <div className="text-sm text-emerald-600">{emp.department}</div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <input type="checkbox" checked={contractorForm.willReturn} onChange={(e) => setContractorForm({...contractorForm, willReturn: e.target.checked})} className="w-5 h-5" />
                  <Label>Will be returning?</Label>
                </div>
                {contractorForm.willReturn && (
                  <div className="grid grid-cols-2 gap-4 p-4 bg-amber-50 rounded-lg">
                    <div>
                      <Label>From Date</Label>
                      <Input 
                        type="date" 
                        value={contractorForm.validFrom} 
                        onChange={(e) => setContractorForm({...contractorForm, validFrom: e.target.value})} 
                        className="h-12" 
                      />
                    </div>
                    <div>
                      <Label>To Date</Label>
                      <Input 
                        type="date" 
                        value={contractorForm.validTo} 
                        onChange={(e) => setContractorForm({...contractorForm, validTo: e.target.value})} 
                        className="h-12" 
                      />
                    </div>
                  </div>
                )}
                <Button onClick={() => handleContractorSignIn(false)} className="w-full h-14 text-xl bg-teal-500 hover:bg-teal-600">Sign In</Button>
              </div>
            )}
            {showReturningContractors && (
              <div>
                <p className="text-emerald-700 mb-4">Select your name:</p>
                {returningContractors.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">No returning contractors</p>
                ) : (
                  <div className="space-y-2">
                    {returningContractors.map((contractor) => (
                      <button key={contractor.id} onClick={() => handleContractorSignIn(true, contractor.id)} className="w-full p-4 bg-emerald-50 hover:bg-emerald-100 rounded-lg text-left">
                        <div className="font-bold text-emerald-900">{contractor.name}</div>
                        <div className="text-sm text-emerald-600">{contractor.company}</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            {signOutMode && (
              <div>
                <p className="text-emerald-700 mb-4">Select a contractor to sign out:</p>
                {activeContractors.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">No active contractors</p>
                ) : (
                  <div className="space-y-2">
                    {activeContractors.map((contractor) => (
                      <button key={contractor.id} onClick={() => handleContractorSignOut(contractor)} className="w-full p-4 bg-emerald-50 hover:bg-emerald-100 rounded-lg text-left">
                        <div className="font-bold text-emerald-900">{contractor.name}</div>
                        <div className="text-sm text-emerald-600">{contractor.company}</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default KioskView;
